// src/components/crm/ReassignFlagModal.js
// PHASE 1.3 VERIFIER: r2-8b1d4f0c
//
// Modal for reassigning an active flag to a different user. Filters out:
//   - The current target  (the CF would reject a no-op reassign)
//
// Self-reassign IS permitted — typical "I'll handle this" workflow when
// someone hands you a flag. (Overrides the original Phase 1.1 'no self-flag'
// rule, alongside the corresponding NoteComposer change.)
//
// Mobile-friendly:
//   - Bottom sheet on small screens
//   - Centered card on >=sm screens
//   - Tap-friendly button + select sizes
//
// ── Defense-in-depth notes ────────────────────────────────────────────────
//   1. The dropdown filter is UX-only — the CF is the authoritative
//      validator. A user who somehow submitted a no-op reassign would be
//      rejected at the CF with HttpsError 'failed-precondition'.
//   2. Submit is disabled until a target is selected — prevents empty-string
//      payloads being sent for the CF to reject.
//   3. isSubmitting guard prevents double-submit; CF transactional state
//      machine is the secondary defense.
//   4. On success the modal closes and the realtime listener reflects the
//      new flagTargetUserId / flagTargetName automatically.
//   5. CF errors surface their HttpsError .message to the user.

import React, { useState, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Flag, X } from 'lucide-react';

const reassignNoteFlagFn = httpsCallable(getFunctions(), 'reassignNoteFlag');

const ReassignFlagModal = ({
  note,
  orgId,
  orgUsers,
  onClose,
}) => {
  const [newFlagTargetUserId, setNewFlagTargetUserId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Drop only the current target — the CF rejects no-op reassigns. Self-
  // reassign is allowed and is the typical "I'll handle this" workflow.
  const eligibleUsers = useMemo(() => {
    return (orgUsers || []).filter(u => u.id !== note.flagTargetUserId);
  }, [orgUsers, note.flagTargetUserId]);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!newFlagTargetUserId) {
      setError('Please select a person to reassign to.');
      return;
    }
    if (!orgId || !note?.id) {
      setError('Missing required information.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await reassignNoteFlagFn({
        orgId,
        noteId: note.id,
        newFlagTargetUserId,
      });
      onClose();
    } catch (err) {
      console.error('ReassignFlagModal: reassign failed', err);
      setError(err?.message || 'Could not reassign flag. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-xl sm:rounded-xl w-full sm:max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-800">Reassign flag</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-slate-400 hover:text-slate-600 p-1 rounded-md disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-1">Currently flagged for</p>
            <p className="text-sm text-slate-700">{note.flagTargetName || 'Unknown'}</p>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">
              Reassign to
            </label>
            <select
              value={newFlagTargetUserId}
              onChange={(e) => setNewFlagTargetUserId(e.target.value)}
              disabled={isSubmitting || eligibleUsers.length === 0}
              className="w-full text-sm border border-slate-200 rounded-lg p-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-amber-300 disabled:opacity-50"
            >
              <option value="">— Select a person —</option>
              {eligibleUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.name}{u.role ? ` (${u.role})` : ''}
                </option>
              ))}
            </select>
            {eligibleUsers.length === 0 && (
              <p className="text-xs text-slate-400 mt-1">
                No other users available to reassign to.
              </p>
            )}
          </div>

          {error && (
            <p className="text-xs text-red-600">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 pt-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-sm font-semibold px-4 py-2 rounded-lg text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !newFlagTargetUserId}
            className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Flag size={14} />
            {isSubmitting ? 'Reassigning…' : 'Reassign'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReassignFlagModal;